import { useMemo } from 'react';
import { formatMultiplier, parsePolicyVersion, tariffVersionNumber } from '../../src/index';
import { useT } from '../i18n/provider';
import { usePlaceNames } from '../i18n/scope-name';
import { useVocab } from '../i18n/vocab';

/**
 * D854 (L7a-027): the ride 360 says a governed-policy version and a tariff version in words ("Revisão da tarifa final ·
 * versão 3 · Paracatu"), never as the stored label (`fare.finalRevision@market:br:paracatu#v3`). A label the helper does not
 * recognise (a dispatch wave, a mode) is printed as it is - nothing is invented. The raw label stays one hover away (`title`).
 */
export function usePolicyVersionText(): {
  readonly policy: (raw: string | undefined) => string;
  readonly tariff: (raw: string | undefined, categoryName: string) => string;
  readonly multiplier: (basisPoints: number) => string;
} {
  const { t, lang } = useT();
  const { v, has } = useVocab();
  const places = usePlaceNames();
  return useMemo(
    () => ({
      policy: (raw) => {
        const parts = parsePolicyVersion(raw);
        if (raw === undefined) return '—';
        if (parts === undefined) return raw;
        const name = has('settings.key', parts.key) ? v('settings.key', parts.key) : parts.key;
        if (parts.origin === 'default') return t('ride.policyText.default', { name });
        if (parts.origin === 'invalid') return t('ride.policyText.invalid', { name });
        const place = parts.origin === 'market' ? places.market(parts.ref) : parts.origin === 'country' ? places.country(parts.ref) : places.region(parts.ref);
        return t('ride.policyText.version', { name, version: parts.version ?? 0, place });
      },
      tariff: (raw, categoryName) => {
        if (raw === undefined || raw === '') return '—';
        const version = tariffVersionNumber(raw);
        return version === undefined ? raw : t('ride.tariffText', { category: categoryName, version });
      },
      multiplier: (basisPoints) => formatMultiplier(basisPoints, lang),
    }),
    [t, lang, v, has, places],
  );
}
